import React, { useEffect, useState } from 'react'
import { getDealOrgUsers, type OrgUser } from './crmApi'
import { BookingModal } from './BookingModal'

/**
 * Users/Contacts tab (Lee/Robert, 3 Aug 2026) — every WeQuote user of this lead's
 * organisation, live-refreshed from Robert's users feed on open, with a Book-meeting
 * button per user (attendee prefilled). Kept in sync hourly + by user_* webhooks.
 */

const STATUS_STYLE: Record<string, { bg: string; fg: string }> = {
  active: { bg: '#DCFCE7', fg: '#166534' },
  disabled: { bg: '#F1F5F9', fg: '#64748B' },
  removed: { bg: '#FEE2E2', fg: '#991B1B' },
}

export const DealUsers: React.FC<{ dealId: string; dealName?: string }> = ({ dealId, dealName }) => {
  const [users, setUsers] = useState<OrgUser[] | null>(null)
  const [refreshed, setRefreshed] = useState(false)
  const [booking, setBooking] = useState<OrgUser | null>(null)

  const load = () => {
    getDealOrgUsers(dealId)
      .then((r) => { setUsers(r.users); setRefreshed(r.refreshed) })
      .catch(() => setUsers([]))
  }
  useEffect(load, [dealId])

  if (users === null) return <p className="text-sm text-txt-tertiary">Loading users…</p>
  if (users.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border p-8 text-center">
        <p className="text-sm text-txt-secondary">No WeQuote users found for this organisation.</p>
        <p className="text-[11px] text-txt-tertiary mt-1">Users appear automatically once the account has people on WeQuote (synced hourly + live on changes).</p>
      </div>
    )
  }

  const fmtLogin = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : 'never'

  return (
    <div className="max-w-[860px]">
      <p className="text-[11px] text-txt-tertiary mb-2">
        {users.length} user{users.length === 1 ? '' : 's'} on this WeQuote organisation
        {refreshed ? ' · refreshed live just now' : ' · from the last sync (live refresh unavailable)'}
      </p>
      <div className="rounded-xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-txt-tertiary border-b border-border bg-surface-secondary">
              <th className="py-2 px-3 font-medium">User</th>
              <th className="py-2 px-3 font-medium">Contact</th>
              <th className="py-2 px-3 font-medium">Role</th>
              <th className="py-2 px-3 font-medium">Status</th>
              <th className="py-2 px-3 font-medium">Last login</th>
              <th className="py-2 px-3 font-medium text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const s = STATUS_STYLE[u.status] || STATUS_STYLE.active
              return (
                <tr key={u.id} className="border-b border-border last:border-0 align-middle">
                  <td className="py-2.5 px-3">
                    <p className="font-medium text-txt-primary">
                      {u.name || u.email || '—'}
                      {u.isAdmin && <span className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-purple-100 text-purple-700">Admin</span>}
                    </p>
                    {u.title && <p className="text-[11px] text-txt-tertiary">{u.title}</p>}
                  </td>
                  <td className="py-2.5 px-3">
                    {u.email && <a href={`mailto:${u.email}`} className="block text-[12px] text-brand-600 hover:underline truncate max-w-[220px]">{u.email}</a>}
                    {u.phone && <a href={`tel:${u.phone.replace(/[\s()-]/g, '')}`} className="block text-[12px] text-txt-secondary hover:text-txt-primary">{u.phone}</a>}
                    {!u.email && !u.phone && <span className="text-txt-tertiary text-[12px]">—</span>}
                  </td>
                  <td className="py-2.5 px-3 text-[12px] text-txt-secondary">{u.role || '—'}</td>
                  <td className="py-2.5 px-3">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: s.bg, color: s.fg }}>{u.status}</span>
                  </td>
                  <td className="py-2.5 px-3 text-[12px] text-txt-tertiary whitespace-nowrap">{fmtLogin(u.lastLoginAt)}</td>
                  <td className="py-2.5 px-3 text-right">
                    <button
                      onClick={() => setBooking(u)}
                      disabled={!u.email}
                      title={u.email ? `Book a meeting with ${u.name || u.email}` : 'No email — cannot invite'}
                      className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-40"
                    >
                      Book meeting
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {booking && booking.email && (
        <BookingModal
          dealId={dealId}
          dealName={dealName}
          initialType="meeting"
          initial={{ title: `Meeting with ${booking.name || booking.email}`, attendees: [booking.email] }}
          onClose={() => setBooking(null)}
          onSaved={() => setBooking(null)}
        />
      )}
    </div>
  )
}
